import * as Sentry from '@sentry/react'
import { createBrowserRouter, Navigate } from 'react-router'

import { ErrorBoundary } from '@/components/ErrorBoundary'
import { Loading } from '@/components/Loading'
import { SubscriptionGuardedPage } from '@/components/SubscriptionGuardedPage'
import { useFeatureGuard } from '@/hooks/useFeatureCheck'
import { useRoleGuard } from '@/hooks/useRoleCheck'
import { AppLayout } from '@/layouts/AppLayout'
import DashboardPage from '@/pages'
import NotFoundPage from '@/pages/404'
import AdminDashboardPage from '@/pages/admin'
import AdminAccountsPage from '@/pages/admin/accounts'
import AdminAccountDetailPage from '@/pages/admin/accounts/[id]'
import AdminUsersPage from '@/pages/admin/users'
import AdminUserDetailPage from '@/pages/admin/users/[id]'
import ErrorPage from '@/pages/auth/error'
import ForgotPasswordPage from '@/pages/auth/forgot-password'
import LoginPage from '@/pages/auth/login'
import ResetPasswordPage from '@/pages/auth/reset-password'
import SignupPage from '@/pages/auth/signup'
import VerifyEmailPage from '@/pages/auth/verify'
import BankPage from '@/pages/bank'
import ErsteBankPage from '@/pages/bank/erste'
import FioBankPage from '@/pages/bank/fio'
import MonetaBankPage from '@/pages/bank/moneta'
import RaiffeisenBankPage from '@/pages/bank/raiffeisen'
import CashRegisterPage from '@/pages/cash-register'
import CashRegisterDashboardPage from '@/pages/cash-register/dashboard'
import CashRegisterEetPage from '@/pages/cash-register/eet'
import CashRegisterReceiptsPage from '@/pages/cash-register/receipts'
import CashRegisterSettingsPage from '@/pages/cash-register/settings'
import CatalogCategoriesPage from '@/pages/catalog/categories'
import ProductEditPage from '@/pages/catalog/product/[id]/edit'
import ProductDetailPage from '@/pages/catalog/product/[id]/index'
import ProductNewPage from '@/pages/catalog/product/new'
import CatalogProductsPage from '@/pages/catalog/products'
import CatalogPropertiesPage from '@/pages/catalog/properties'
import ContactDetailPage from '@/pages/contact/[id]'
import ContactEditPage from '@/pages/contact/[id]/edit'
import ContactNewPage from '@/pages/contact/new'
import ContactsPage from '@/pages/contacts'
import EshopDeliveryMethodsPage from '@/pages/eshop/delivery-methods'
import EshopPaymentMethodsPage from '@/pages/eshop/payment-methods'
import EshopPickupPointsPage from '@/pages/eshop/pickup-points'
import EshopTesterPage from '@/pages/eshop/tester'
import EshopTokensPage from '@/pages/eshop/tokens'
import ExpenseDetailPage from '@/pages/expense/[id]'
import ExpenseEditPage from '@/pages/expense/[id]/edit'
import ExpenseNewPage from '@/pages/expense/new'
import ExpenseUploadPage from '@/pages/expense/upload'
import ExpensesPage from '@/pages/expenses'
import InvoiceDetailPage from '@/pages/invoice/[id]'
import InvoiceEditPage from '@/pages/invoice/[id]/edit'
import InvoiceSendPage from '@/pages/invoice/[id]/send'
import InvoiceNewPage from '@/pages/invoice/new'
import InvoicesPage from '@/pages/invoices'
import MaintenancePage from '@/pages/maintenance'
import OAuthAuthorizePage from '@/pages/oauth/authorize'
import OrderDetailPage from '@/pages/order/[id]'
import OrderEditPage from '@/pages/order/[id]/edit'
import OrderSendPage from '@/pages/order/[id]/send'
import OrderNewPage from '@/pages/order/new'
import OrdersPage from '@/pages/orders'
import SettingsPage from '@/pages/settings'
import SettingsAccountPage from '@/pages/settings/account'
import SettingsApiPage from '@/pages/settings/api'
import SettingsBankAccountsPage from '@/pages/settings/bank-accounts'
import SettingsBankNotificationsPage from '@/pages/settings/bank-notifications'
import SettingsCurrenciesPage from '@/pages/settings/currencies'
import SettingsDocumentSequencesPage from '@/pages/settings/document-sequences'
import SettingsEshopPage from '@/pages/settings/eshop'
import SettingsFakturoidImportPage from '@/pages/settings/fakturoid-import'
import SettingsFootnotePage from '@/pages/settings/footnote'
import SettingsInvoicingPage from '@/pages/settings/invoicing'
import SettingsSignaturePage from '@/pages/settings/signature'
import SettingsSupplierPage from '@/pages/settings/supplier'
import SettingsUsersPage from '@/pages/settings/users'
import SubscriptionPage from '@/pages/subscription'
import SetPasswordPage from '@/pages/user/set-password'
import VerifyPage from '@/pages/verify'
import WarehouseDetailPage from '@/pages/warehouse/warehouse/[id]/index'
import WarehousesPage from '@/pages/warehouse/warehouses'
import { Role, RoleType } from '@/types/User'

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV7(createBrowserRouter)

const FeatureGuardedPage = ({
  feature,
  children,
}: {
  feature: string
  children: React.ReactNode
}) => {
  const { isLoading: isFeatureLoading } = useFeatureGuard(feature, '/')
  return isFeatureLoading ? <Loading /> : children
}

const RoleGuardedPage = ({
  roles,
  redirectTo,
  children,
}: {
  roles: RoleType[]
  redirectTo: string
  children: React.ReactNode
}) => {
  const { isAllowed, isLoading } = useRoleGuard(roles)

  if (isLoading) {
    return <Loading />
  }

  if (!isAllowed) {
    return <Navigate to={redirectTo} />
  }

  return children
}

export const router = sentryCreateBrowserRouter(
  [
    {
      element: <AppLayout />,
      errorElement: <ErrorBoundary />,
      children: [
        {
          path: '/',
          element: <RoleGuardedPage roles={[Role.owner, Role.manager, Role.employee, Role.accountant]} redirectTo="/admin">
            <DashboardPage />
          </RoleGuardedPage>,
        },
        {
          path: '/admin/users',
          element: <RoleGuardedPage roles={[Role.admin]} redirectTo="/">
            <AdminUsersPage />
          </RoleGuardedPage>,
        },
        {
          path: '/admin/users/:id',
          element: <RoleGuardedPage roles={[Role.admin]} redirectTo="/">
            <AdminUserDetailPage />
          </RoleGuardedPage>,
        },
        {
          path: '/admin/accounts',
          element: <RoleGuardedPage roles={[Role.admin]} redirectTo="/">
            <AdminAccountsPage />
          </RoleGuardedPage>,
        },
        {
          path: '/admin/accounts/:id',
          element: <RoleGuardedPage roles={[Role.admin]} redirectTo="/">
            <AdminAccountDetailPage />
          </RoleGuardedPage>,
        },
        {
          path: '/admin',
          element: <RoleGuardedPage roles={[Role.admin]} redirectTo="/">
            <AdminDashboardPage />
          </RoleGuardedPage>,
        },
        { path: '/invoices', element: <InvoicesPage /> },
        { path: '/invoice/new', element: <SubscriptionGuardedPage redirectTo="/invoices"><InvoiceNewPage /></SubscriptionGuardedPage> },
        { path: '/invoice/:id', element: <InvoiceDetailPage /> },
        { path: '/invoice/:id/edit', element: <SubscriptionGuardedPage redirectTo={(params) => `/invoice/${params.id}`}><InvoiceEditPage /></SubscriptionGuardedPage> },
        { path: '/invoice/:id/send', element: <InvoiceSendPage /> },
        { path: '/orders', element: <OrdersPage /> },
        { path: '/order/new', element: <SubscriptionGuardedPage redirectTo="/orders"><OrderNewPage /></SubscriptionGuardedPage> },
        { path: '/order/:id', element: <OrderDetailPage /> },
        { path: '/order/:id/edit', element: <SubscriptionGuardedPage redirectTo={(params) => `/order/${params.id}`}><OrderEditPage /></SubscriptionGuardedPage> },
        { path: '/order/:id/send', element: <OrderSendPage /> },
        { path: '/expenses', element: <ExpensesPage /> },
        { path: '/expense/new', element: <SubscriptionGuardedPage redirectTo="/expenses"><ExpenseNewPage /></SubscriptionGuardedPage> },
        { path: '/expense/upload', element: <SubscriptionGuardedPage redirectTo="/expenses"><ExpenseUploadPage /></SubscriptionGuardedPage> },
        { path: '/expense/:id', element: <ExpenseDetailPage /> },
        { path: '/expense/:id/edit', element: <SubscriptionGuardedPage redirectTo={(params) => `/expense/${params.id}`}><ExpenseEditPage /></SubscriptionGuardedPage> },
        { path: '/contacts', element: <ContactsPage /> },
        { path: '/contact/new', element: <ContactNewPage /> },
        { path: '/contact/:id', element: <ContactDetailPage /> },
        { path: '/contact/:id/edit', element: <ContactEditPage /> },
        { path: '/settings', element: <SettingsPage /> },
        { path: '/settings/currencies', element: <SettingsCurrenciesPage /> },
        { path: '/settings/document-sequences', element: <SettingsDocumentSequencesPage /> },
        { path: '/settings/signature', element: <SettingsSignaturePage /> },
        { path: '/settings/fakturoid-import', element: <SettingsFakturoidImportPage /> },
        { path: '/settings/account', element: <SettingsAccountPage /> },
        { path: '/settings/api', element: <SettingsApiPage /> },
        { path: '/settings/footnote', element: <SettingsFootnotePage /> },
        { path: '/settings/invoicing', element: <SettingsInvoicingPage /> },
        { path: '/settings/supplier', element: <SettingsSupplierPage /> },
        { path: '/settings/bank-accounts', element: <SettingsBankAccountsPage /> },
        { path: '/settings/bank-notifications', element: <SettingsBankNotificationsPage /> },
        { path: '/settings/users', element: <SettingsUsersPage /> },
        { path: '/settings/eshop', element: <FeatureGuardedPage feature="eshop"><SettingsEshopPage /></FeatureGuardedPage> },
        { path: '/404', element: <NotFoundPage /> },
        {
          path: '/bank',
          element: <FeatureGuardedPage feature="bank"><BankPage /></FeatureGuardedPage>,
        },
        {
          path: '/bank/fio',
          element: <FeatureGuardedPage feature="bank"><FioBankPage /></FeatureGuardedPage>,
        },
        {
          path: '/bank/moneta',
          element: <FeatureGuardedPage feature="bank"><MonetaBankPage /></FeatureGuardedPage>,
        },
        {
          path: '/bank/raiffeisen',
          element: <FeatureGuardedPage feature="bank"><RaiffeisenBankPage /></FeatureGuardedPage>,
        },
        {
          path: '/bank/erste',
          element: <FeatureGuardedPage feature="bank"><ErsteBankPage /></FeatureGuardedPage>,
        },
        {
          path: '/cash-register/dashboard',
          element: <FeatureGuardedPage feature="cashRegistry"><CashRegisterDashboardPage /></FeatureGuardedPage>,
        },
        {
          path: '/cash-register/settings',
          element: <FeatureGuardedPage feature="cashRegistry"><CashRegisterSettingsPage /></FeatureGuardedPage>,
        },
        {
          path: '/cash-register/receipts',
          element: <FeatureGuardedPage feature="cashRegistry"><CashRegisterReceiptsPage /></FeatureGuardedPage>,
        },
        {
          path: '/cash-register/eet',
          element: <FeatureGuardedPage feature="cashRegistry"><CashRegisterEetPage /></FeatureGuardedPage>,
        },
        {
          path: '/cash-register',
          element: <FeatureGuardedPage feature="cashRegistry"><CashRegisterPage /></FeatureGuardedPage>,
        },
        { path: '/catalog/categories', element: <CatalogCategoriesPage /> },
        { path: '/catalog/products', element: <CatalogProductsPage /> },
        { path: '/catalog/properties', element: <CatalogPropertiesPage /> },
        { path: '/catalog/variant-axes', element: <Navigate to="/catalog/properties?tab=variants" replace /> },
        { path: '/catalog/parameters', element: <Navigate to="/catalog/properties?tab=parameters" replace /> },
        { path: '/catalog/product/new', element: <ProductNewPage /> },
        { path: '/catalog/product/:id', element: <ProductDetailPage /> },
        { path: '/catalog/product/:id/edit', element: <ProductEditPage /> },
        { path: '/eshop/delivery-methods', element: <FeatureGuardedPage feature="eshop"><EshopDeliveryMethodsPage /></FeatureGuardedPage> },
        { path: '/eshop/payment-methods', element: <FeatureGuardedPage feature="eshop"><EshopPaymentMethodsPage /></FeatureGuardedPage> },
        { path: '/eshop/pickup-points', element: <FeatureGuardedPage feature="eshop"><EshopPickupPointsPage /></FeatureGuardedPage> },
        { path: '/eshop/tokens', element: <FeatureGuardedPage feature="eshop"><EshopTokensPage /></FeatureGuardedPage> },
        { path: '/eshop/tester', element: <FeatureGuardedPage feature="eshop"><EshopTesterPage /></FeatureGuardedPage> },
        { path: '/warehouse/warehouses', element: <FeatureGuardedPage feature="warehouse"><WarehousesPage /></FeatureGuardedPage> },
        { path: '/warehouse/warehouse/:id', element: <FeatureGuardedPage feature="warehouse"><WarehouseDetailPage /></FeatureGuardedPage> },
        { path: '/subscription', element: <SubscriptionPage /> },
        { path: '*', element: <NotFoundPage /> },
      ],
    },
    { path: '/auth/login', element: <LoginPage /> },
    { path: '/auth/signup', element: <SignupPage /> },
    { path: '/auth/verify', element: <VerifyEmailPage /> },
    { path: '/auth/forgot-password', element: <ForgotPasswordPage /> },
    { path: '/auth/reset-password', element: <ResetPasswordPage /> },
    { path: '/auth/error', element: <ErrorPage /> },
    { path: '/verify', element: <VerifyPage /> },
    { path: '/user/set-password', element: <SetPasswordPage /> },
    { path: '/maintenance', element: <MaintenancePage /> },
    { path: '/oauth/authorize', element: <OAuthAuthorizePage /> },
  ],
  {
    errorElement: <ErrorBoundary />,
  },
)
